<template>
  <div class="spinner-container" :style="containerStyle">
    <svg
      :width="containerStyle.width"
      :height="containerStyle.height"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      class="spinner"
      :style="{ animationDuration: `${speed}s` }"
    >
      <circle cx="12" cy="12" r="10" stroke="url(#paint0_linear_16855_200307)" style="" stroke-width="4" />
      <defs>
        <linearGradient id="paint0_linear_16855_200307" x1="12" y1="2" x2="12" y2="22" gradientUnits="userSpaceOnUse">
          <stop :stop-color="colors.start" style="stop-opacity: 1" />
          <stop offset="1" :stop-color="colors.end" style="stop-opacity: 1" />
        </linearGradient>
      </defs>
    </svg>
  </div>
</template>

<script setup lang="ts">
  defineOptions({
    name: "Spinner",
  });

  interface SpinnerProps {
    size?: number | string;
    primaryColor?: string;
    secondaryColor?: string;
    swapColors?: boolean;
    speed?: number;
    respectTheme?: boolean;
  }

  const {
    size = 20,
    primaryColor = "#42464C",
    secondaryColor = "white",
    swapColors = false,
    speed = 1.5,
    respectTheme = true,
  } = defineProps<SpinnerProps>();

  const containerStyle = computed(() => ({
    width: typeof size === "number" ? `${size}px` : size,
    height: typeof size === "number" ? `${size}px` : size,
  }));

  const themeIsDark = computed(() => {
    if (!respectTheme || typeof document === "undefined") return false;
    const htmlElement = document.documentElement;
    const theme = htmlElement.getAttribute("data-theme");
    return theme === "dark";
  });

  const colors = computed(() => {
    const shouldSwapColors = themeIsDark.value;
    return shouldSwapColors
      ? { start: secondaryColor, end: primaryColor }
      : { start: primaryColor, end: secondaryColor };
  });

  // Add theme watcher to update when theme changes
  let observer: MutationObserver | undefined;

  onMounted(() => {
    if (respectTheme && typeof document !== "undefined") {
      observer = new MutationObserver(() => {
        // Force reactive update when theme attribute changes
        themeIsDark.value; // This is accessed just to trigger reactivity
      });

      observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-theme"],
      });
    }
  });

  onBeforeUnmount(() => {
    if (observer) {
      observer.disconnect();
    }
  });
</script>

<style scoped>
  .spinner-container {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .spinner {
    animation: spin linear infinite;
    transform-origin: center;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
